import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useScrollToTopWhen } from '../../hooks/useScrollToTop';
import { getAuthToken } from '../../api/services/authService';
import Header from '../../components/Header/Header';
import './TenantDashboard.css';
import Home from './components/Home';
import Listings from './components/Listings';
import Score from './components/Score';
import ScoreDetails from './components/ScoreDetails';
import Profile from '../Profile/Profile';
import Notifications from '../shared/Notifications/Notifications';
import PropertyDetails from './components/PropertyDetails';
import YourHome from './components/YourHome';
import UnitRequests from './components/UnitRequests';
import BnbBookings from './components/BnbBookings';
import PaymentHistory from './components/PaymentHistory';
import TenancyHistory from './components/TenancyHistory';
import MyBills from './components/MyBills';
import Maintenance from './components/Maintenance';
import MoveOutRequests from './components/MoveOutRequests';
import Messages from '../shared/Messages/Messages';
import MyReports from './components/MyReports';
import AccountHub from './components/AccountHub';


import MobileBottomNav from '../../components/MobileBottomNav/MobileBottomNav';

const TenantDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('home');
  const [navigationState, setNavigationState] = useState({});

  useScrollToTopWhen(activeSection, location.pathname);

  // Check if we're on a property details page
  const isPropertyDetails = location.pathname.startsWith('/tenant/property/');

  // Check if we're on the your home page
  const isYourHome = location.pathname === '/tenant/your-home';

  // Check if we're on the unit requests page
  const isUnitRequests = location.pathname === '/tenant/requests' || location.pathname.startsWith('/tenant/requests');

  const handleScoreDetailsBack = useCallback(() => {
    setActiveSection('score');
  }, []);

  useEffect(() => {
    // If guest tries to access /tenant/property, redirect to public route /property/:id
    if (isPropertyDetails && !getAuthToken()) {
      const parts = location.pathname.split('/');
      const propertyId = parts[parts.length - 1];
      if (propertyId) {
        navigate(`/property/${propertyId}${location.search}`, { replace: true });
      }
    }
  }, [isPropertyDetails, location.pathname, location.search, navigate]);

  const handleSidebarClick = (section, state = {}) => {
    setActiveSection(section);
    setNavigationState(state);
    navigate(section === 'home' ? '/tenant' : `/tenant/${section}`, { state });
  };

  useEffect(() => {
    // Update activeSection based on URL
    if (isPropertyDetails || isYourHome) {
      // Don't change activeSection for property details or your home
      return;
    }
    if (isUnitRequests) {
      setActiveSection('requests');
      return;
    }
    const path = location.pathname;
    if (path === '/tenant' || path === '/tenant/') {
      setActiveSection('home');
    } else if (path.includes('/listings')) {
      setActiveSection('listings');
    } else if (path.includes('/requests')) {
      setActiveSection('requests');
    } else if (path.includes('/score')) {
      setActiveSection('score');
    } else if (path.includes('/bookings')) {
      setActiveSection('bookings');
    } else if (path.includes('/payment-history')) {
      setActiveSection('payment-history');
    } else if (path.includes('/maintenance')) {
      setActiveSection('maintenance');
    } else if (path.includes('/my-reports')) {
      setActiveSection('my-reports');
    } else if (path.includes('/my-bills')) {
      setActiveSection('my-bills');
    } else if (path.includes('/move-out-requests')) {
      setActiveSection('move-out-requests');
    } else if (path.includes('/tenancy-history')) {
      setActiveSection('tenancy-history');
    } else if (path.includes('/account')) {
      setActiveSection('account');
    } else if (path.includes('/profile')) {
      setActiveSection('profile');
    } else if (path.includes('/messages')) {
      setActiveSection('messages');
    } else if (path.includes('/notifications')) {
      setActiveSection('notifications');
    }
  }, [location.pathname, isPropertyDetails, isYourHome, isUnitRequests]);

  const renderContent = () => {
    // Render PropertyDetails if on property details page
    if (isPropertyDetails) {
      return <PropertyDetails />;
    }

    // Render YourHome if on your home page
    if (isYourHome) {
      return <YourHome />;
    }

    // Render UnitRequests if on unit requests page
    if (isUnitRequests || activeSection === 'requests') {
      return <UnitRequests />;
    }

    switch (activeSection) {
      case 'home':
        return <Home />;
      case 'listings':
        return <Listings />;
      case 'score':
        return <Score onViewHistory={() => setActiveSection('score-details')} />;
      case 'score-details':
        return <ScoreDetails onBack={handleScoreDetailsBack} />;
      case 'payment-history':
        return <PaymentHistory />;
      case 'bookings':
        return <BnbBookings />;
      case 'maintenance':
        return <Maintenance />;
      case 'my-reports':
        return <MyReports />;
      case 'my-bills':
        return <MyBills />;
      case 'tenancy-history':
        return <TenancyHistory />;
      case 'move-out-requests':
        return <MoveOutRequests />;
      case 'account':
        return <AccountHub />;
      case 'profile':
        return <Profile />;
      case 'messages':
        return <Messages userRole="Tenant" threadId={navigationState?.threadId} />;
      case 'notifications':
        return <Notifications onNavigate={handleSidebarClick} userRole="Tenant" />;
      default:
        return <Home />;
    }
  };

  return (
    <div className={`dashboard dashboard--tenant ${isPropertyDetails ? 'property-details-active' : ''}`}>
      <div className={`dashboard-content ${isPropertyDetails ? 'property-details-content' : ''}`}>
        <Header
          userRole="Tenant"
          activeSection={activeSection}
          onNavClick={(section) => {
            // Map 'overview' to 'home' for Tenant dashboard
            if (section === 'overview') {
              setActiveSection('home');
            } else {
              setActiveSection(section);
            }
          }}
        />
        <main className={`dashboard-main ${isPropertyDetails ? 'property-details-main' : ''}`}>
          {renderContent()}
        </main>
        {!isPropertyDetails && (
          <MobileBottomNav
            activeSection={isYourHome ? 'your-home' : activeSection}
            onNavClick={(section) => {
              if (section === 'your-home') {
                navigate('/tenant/your-home');
              } else {
                handleSidebarClick(section);
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

export default TenantDashboard;
